import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI } from '../../utils/fetchData';

const MetricCard = ({ title, value, subtitle, color = '#17A2B8' }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-2">
            <div 
                className="w-[8px] h-[8px] rounded-full"
                style={{ backgroundColor: color }}
            />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        <div className="text-white text-[18px] font-bold mb-1">{value}</div>
        {subtitle && (
            <div className="text-[rgba(255,255,255,0.5)] text-[10px]">{subtitle}</div>
        )}
    </div>
);

const ContentCard = ({ title, children }) => (
    <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[rgba(255,255,255,0.2)] transition-all">
        <div className="flex items-center gap-2 mb-4">
            <div className="w-[8px] h-[8px] rounded-full bg-[#17A2B8]" />
            <span className="text-[10px] text-[rgba(255,255,255,0.7)] uppercase tracking-wider">
                {title}
            </span>
        </div>
        {children}
    </div>
);

const CatalogAnalytics = () => {
    const navigate = useNavigate();
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const token = localStorage.getItem('dizeeToken');

    useEffect(() => {
        loadAnalytics();
    }, [filter]);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const response = await getDataAPI(`user/analytics/catalog?filter=${filter}`, token);
            setAnalyticsData(response.data.data.response);
        } catch (error) {
            console.error('Failed to load analytics:', error);
            setAnalyticsData(null);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (!num || isNaN(num)) return '0';
        return new Intl.NumberFormat('en-US').format(num);
    };

    const getReleaseStatusColor = (status) => {
        switch (status) {
            case 'released': return '#34C759';
            case 'pending': return '#FF9500';
            case 'unreleased': return '#8E8E93';
            default: return '#8E8E93';
        }
    };

    if (loading) {
        return (
            <LayoutHeader>
                <div className="w-[390px] h-screen bg-black text-white flex items-center justify-center">
                    <p className="text-[12px]">Loading analytics...</p>
                </div>
            </LayoutHeader>
        );
    }

    return (
        <LayoutHeader>
            <div className="w-[390px] h-screen bg-black text-white relative">
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between">
                        <div className="flex items-center gap-2">
                            <div 
                                className="w-[24px] h-[24px] rounded-md"
                                style={{ backgroundColor: '#17A2B8' }}
                            />
                            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Catalog Analytics</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <p className='text-[#17A2B8] cursor-pointer text-[12px] hover:opacity-70 transition-opacity'>Export data</p>
                            <div 
                                className="text-white cursor-pointer text-[12px] hover:text-gray-400 transition-colors"
                                onClick={() => navigate('/analytics/hub')}
                            >
                                Back
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="w-full px-4 flex-1 overflow-y-auto pb-[100px]">
                            {/* Filter Dropdown */}
                            <div className="mb-6">
                                <select 
                                    value={filter}
                                    onChange={(e) => setFilter(e.target.value)}
                                    className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-white text-[12px] px-4 py-3 rounded-lg focus:outline-none focus:border-[#17A2B8] transition-colors appearance-none cursor-pointer"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='white' stroke-opacity='0.5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 1rem center'
                                    }}
                                >
                                    <option value="all" className="bg-black">All Time</option>
                                    <option value="day" className="bg-black">Last 24 Hours</option>
                                    <option value="week" className="bg-black">Last Week</option>
                                    <option value="month" className="bg-black">Last Month</option>
                                    <option value="year" className="bg-black">Last Year</option>
                                </select>
                            </div>

                        {analyticsData ? (
                            <>
                                {/* Metrics Grid */}
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <MetricCard 
                                        title="Total Streams" 
                                        value={formatNumber(analyticsData.totalStreams)}
                                        subtitle="Link interactions"
                                    />
                                    <MetricCard 
                                        title="Total Tracks" 
                                        value={formatNumber(analyticsData.totalTracks)}
                                        subtitle={`${formatNumber(analyticsData.releasedTracks)} released`}
                                    />
                                    <MetricCard 
                                        title="Total Playlists" 
                                        value={formatNumber(analyticsData.totalPlaylists)}
                                        subtitle="Active playlists"
                                    />
                                    <MetricCard 
                                        title="Unreleased" 
                                        value={formatNumber(analyticsData.unreleasedTracks)}
                                        subtitle={`${formatNumber(analyticsData.pendingTracks)} pending`}
                                    />
                                </div>

                                {/* Chart Placeholder */}
                                <div className="bg-[#1A1A1A] rounded-lg p-4 border border-[#333] mb-6">
                                    <h3 className="text-white text-[12px] mb-3">Streaming Trends</h3>
                                    <div className="h-[120px] bg-[#0A0A0A] rounded flex items-center justify-center">
                                        <p className="text-gray-500 text-[10px]">CHART</p>
                                    </div>
                                </div>

                                {/* Platform Distribution */}
                                {analyticsData?.platformDistribution && (
                                    <ContentCard title="Platform Distribution">
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-gray-400">Spotify</span>
                                                <span className="text-[10px] text-white">{analyticsData.platformDistribution.spotify} tracks</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-gray-400">Apple Music</span>
                                                <span className="text-[10px] text-white">{analyticsData.platformDistribution.appleMusic} tracks</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-gray-400">YouTube</span>
                                                <span className="text-[10px] text-white">{analyticsData.platformDistribution.youtube} tracks</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] text-gray-400">SoundCloud</span>
                                                <span className="text-[10px] text-white">{analyticsData.platformDistribution.soundcloud} tracks</span>
                                            </div>
                                        </div>
                                    </ContentCard>
                                )}

                                {/* Genre Performance */}
                                {analyticsData?.genrePerformance && analyticsData.genrePerformance.length > 0 && (
                                    <div className="mt-4">
                                        <ContentCard title="Top Genres">
                                            <div className="space-y-3">
                                                {analyticsData.genrePerformance.map((genre, index) => (
                                                    <div key={index} className="flex justify-between items-center">
                                                        <span className="text-[10px] text-white">{genre.genre}</span>
                                                        <span className="text-[10px] text-gray-400">{genre.tracks} tracks</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </ContentCard>
                                    </div>
                                )}

                                {/* Label Distribution */}
                                {analyticsData?.labelDistribution && analyticsData.labelDistribution.length > 0 && (
                                    <div className="mt-4">
                                        <ContentCard title="Top Labels">
                                            <div className="space-y-3">
                                                {analyticsData.labelDistribution.map((label, index) => (
                                                    <div key={index} className="flex justify-between items-center">
                                                        <span className="text-[10px] text-white">{label.label}</span>
                                                        <span className="text-[10px] text-gray-400">{label.tracks} tracks</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </ContentCard>
                                    </div>
                                )}

                                {/* Distributor Breakdown */}
                                {analyticsData?.distributorBreakdown && analyticsData.distributorBreakdown.length > 0 && (
                                    <div className="mt-4">
                                        <ContentCard title="Top Distributors">
                                            <div className="space-y-3">
                                                {analyticsData.distributorBreakdown.map((distributor, index) => (
                                                    <div key={index} className="flex justify-between items-center">
                                                        <span className="text-[10px] text-white">{distributor.distributor}</span>
                                                        <span className="text-[10px] text-gray-400">{distributor.tracks} tracks</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </ContentCard>
                                    </div>
                                )}

                                {/* Top Tracks */}
                                {analyticsData?.topTracks && analyticsData.topTracks.length > 0 && (
                                    <div className="mt-4">
                                        <ContentCard title="Top Performing Tracks">
                                            <div className="space-y-3">
                                                {analyticsData.topTracks.map((track, index) => (
                                                    <div key={index} className="flex justify-between items-center">
                                                        <div>
                                                            <span className="text-[10px] text-white">{track.title}</span>
                                                            <span className="text-[8px] text-gray-400 ml-2">{track.clickCount} streams</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </ContentCard>
                                    </div>
                                )}

                                {/* Top Playlists */}
                                {analyticsData?.topPlaylists && analyticsData.topPlaylists.length > 0 && (
                                    <div className="mt-4">
                                        <ContentCard title="Top Performing Playlists">
                                            <div className="space-y-3">
                                                {analyticsData.topPlaylists.map((playlist, index) => (
                                                    <div key={index} className="flex justify-between items-center">
                                                        <div>
                                                            <span className="text-[10px] text-white">{playlist.title}</span>
                                                            <span className="text-[8px] text-gray-400 ml-2">{playlist.clickCount} plays</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </ContentCard>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="py-[40px]">
                                <h3 className="text-white text-[12px] font-normal mb-2">No catalog data available</h3>
                                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6">
                                    Import your music catalog to see detailed analytics.
                                </p>
                                <button 
                                    onClick={() => navigate('/catalog')}
                                    className="bg-[#17A2B8] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#138A9F] transition-colors"
                                >
                                    Import Catalog
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Help Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 h-[100px] bg-black flex items-end justify-center pb-4">
                        <p className="text-gray-400 text-[10px] text-center px-4">
                            Monitor your music catalog performance, streaming analytics, and platform distribution based on your actual catalog data.
                        </p>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default CatalogAnalytics; 